import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { wishlistAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    try {
      setLoading(true);
      const response = await wishlistAPI.get();
      setWishlist(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      // Fallback to localStorage if API fails
      const stored = localStorage.getItem('pop_wishlist');
      if (stored) setWishlist(JSON.parse(stored));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Sync to localStorage as backup
  useEffect(() => {
    if (wishlist.length > 0) {
      localStorage.setItem('pop_wishlist', JSON.stringify(wishlist));
    }
  }, [wishlist]);

  const addToWishlist = useCallback(async (product) => {
    try {
      if (!wishlist.find(item => item.id === product.id)) {
        await wishlistAPI.add(product.id);
        await fetchWishlist();
        toast.success('Added to wishlist!');
      }
      return true;
    } catch (error) {
      console.error('Add to wishlist error:', error);
      // Optimistic update for local fallback
      setWishlist(prev => [...prev, product]);
      return false;
    }
  }, [wishlist, fetchWishlist]);

  const removeFromWishlist = useCallback(async (productId) => {
    try {
      await wishlistAPI.remove(productId);
      await fetchWishlist();
      toast.success('Removed from wishlist');
    } catch (error) {
      console.error('Remove from wishlist error:', error);
      setWishlist(prev => prev.filter(item => item.id !== productId));
    }
  }, [fetchWishlist]);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => item.id === productId);
  }, [wishlist]);

  const toggleWishlist = useCallback(async (product) => {
    if (wishlist.some(item => item.id === product.id)) {
      await removeFromWishlist(product.id);
    } else {
      await addToWishlist(product);
    }
  }, [wishlist, addToWishlist, removeFromWishlist]);

  const value = useMemo(() => ({
    wishlist,
    loading,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    toggleWishlist,
    fetchWishlist
  }), [wishlist, loading, addToWishlist, removeFromWishlist, isInWishlist, toggleWishlist, fetchWishlist]);

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
